import client from '../../../api/client';

/**
 * Retrieves all documents belonging to a specific workspace manifest context.
 * @param {string} workspaceId - The unique workspace identifier.
 * @returns {Promise<Array>} The list of documents.
 */
export const getDocuments = async (workspaceId) => {
  const response = await client.get(`/api/workspace/${workspaceId}`);
  return response.data.documents || [];
};

/**
 * Retrieves the full content and metadata for a single document.
 * @param {string} id - The document ID.
 * @param {string} type - The document type ('resume', 'variant', 'cover_letter').
 * @returns {Promise<Object>} The document payload.
 */
export const getDocument = async (id, type) => {
  const response = await client.get('/api/document', {
    params: {
      active_document_id: id,
      active_document_type: type,
    },
  });
  return response.data;
};

/**
 * Creates a new document inside the target workspace.
 * @param {string} workspaceId - The workspace context.
 * @param {string} type - Polymorphic type ('resume', 'variant', 'cover_letter').
 * @param {string} name - Desired name for the document.
 * @param {Object} [extra] - Additional parameter payload (e.g. resume_id, overrides).
 * @returns {Promise<Object>} The created document confirmation.
 */
export const createDocument = async (workspaceId, type, name, extra = {}) => {
  const response = await client.post('/api/document', {
    workspace_id: workspaceId,
    type,
    name,
    ...extra,
  });
  return response.data;
};

/**
 * Updates a document using REST API rules per type.
 * @param {string} id - The document ID.
 * @param {string} type - The document type.
 * @param {Object} data - Payload containing update attributes.
 * @returns {Promise<Object>} The updated document response.
 */
export const updateDocument = async (id, type, data) => {
  let response;
  if (type === 'variant') {
    response = await client.patch(`/api/variants/${id}`, data);
  } else if (type === 'cover_letter') {
    response = await client.patch(`/api/cover-letter/${id}`, data);
  } else {
    response = await client.patch(`/api/document/${id}`, data);
  }
  return response.data;
};

/**
 * Deletes the specified document.
 * @param {string} id - The document ID.
 * @returns {Promise<Object>} The deletion confirmation.
 */
export const deleteDocument = async (id) => {
  const response = await client.delete(`/api/document/${id}`);
  return response.data;
};

/**
 * Duplicates the specified document.
 * @param {string} id - The document ID.
 * @param {string} type - The document type.
 * @returns {Promise<Object>} The duplicated document response.
 */
export const duplicateDocument = async (id, type) => {
  let url = '';
  if (type === 'variant') {
    url = `/api/variants/${id}/duplicate`;
  } else if (type === 'cover_letter') {
    url = `/api/cover-letter/${id}/duplicate`;
  } else {
    url = `/api/document/${id}/duplicate`;
  }
  const response = await client.post(url);
  return response.data;
};
